import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import {
  getCartOrderSummary,
  applyDiscountCode,
  removeDiscountCode,
} from '../../CartService';

const SummaryBox = (): JSX.Element => {
  const [cartSummary, setCartSummary] = useState({
    cartId: null,
    subtotal: 0,
    discountCode: '',
    discountAmount: 0,
    vatAmount: 0,
    total: 0,
  });
  const [discountCode, setDiscountCode] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCartSummary();
  }, []);

  const fetchCartSummary = () => {
    setLoading(true);
    getCartOrderSummary()
      .then((response) => {
        setCartSummary(response.data);
        setDiscountCode(response.data.discountCode || '');
      })
      .catch((error) => console.error(error))
      .finally(() => setLoading(false));
  };

  const validateDiscountCode = (code: string): boolean => {
    // Add validation logic (e.g., regex or API-based validation)
    return code.trim().length > 0; // Example: code must not be empty
  };

  const handleApplyDiscountCode = () => {
    if (!validateDiscountCode(discountCode)) {
      alert('Discount Code Invalid');
      return;
    }

    setLoading(true);
    applyDiscountCode(discountCode)
      .then(() => {
        alert('Discount code applied successfully!');
        fetchCartSummary(); // Refresh the cart summary
      })
      .catch((error) => {
        console.error('Error applying discount code:', error);

        // Check if error indicates invalid discount code
        if (error.response?.status === 400) {
          alert('Discount Code Invalid');
        }
        // Handle session expiry
        else if (
          error.response?.status === 401 ||
          error.response?.status === 403
        ) {
          alert('Session has expired. Please log in again.');
          window.location.href = '/login';
        }
        // General error fallback
        else {
          alert('Discount code invalid. Please check code and try again.');
        }
      })
      .finally(() => setLoading(false));
  };

  const handleRemoveDiscountCode = () => {
    setLoading(true);
    removeDiscountCode()
      .then(() => {
        alert('Discount code removed successfully!');
        fetchCartSummary(); // Refresh the cart summary
      })
      .catch((error) => {
        console.error('Error removing discount code:', error);
        alert('Failed to remove discount code. Please try again later.');
      })
      .finally(() => setLoading(false));
  };

  return (
    <Box>
      <Box
        component={'form'}
        noValidate
        autoComplete="off"
        sx={{
          marginY: 4,
          '& .MuiInputBase-input.MuiOutlinedInput-input': {
            bgcolor: 'background.paper',
          },
        }}
      >
        <Box>
          <TextField
            label="Discount code"
            variant="outlined"
            color="primary"
            fullWidth
            value={discountCode}
            onChange={(e) => setDiscountCode(e.target.value)}
            disabled={!!cartSummary.discountCode || loading}
          />
          {cartSummary.discountCode ? (
            <Button
              color="secondary"
              size="large"
              fullWidth
              sx={{
                bgcolor: 'divider',
                color: 'text.primary',
                marginTop: 1,
              }}
              onClick={handleRemoveDiscountCode}
              disabled={loading}
            >
              Remove code
            </Button>
          ) : (
            <Button
              color="primary"
              size="large"
              fullWidth
              sx={{
                bgcolor: 'divider',
                color: 'text.primary',
                marginTop: 1,
              }}
              onClick={handleApplyDiscountCode}
              disabled={loading}
            >
              Apply this code
            </Button>
          )}
        </Box>
      </Box>
      <Stack spacing={2} marginY={{ xs: 2, sm: 4 }}>
        <Box display={'flex'} justifyContent={'space-between'}>
          <Typography color={'text.secondary'}>Subtotal</Typography>
          <Typography color={'text.secondary'} fontWeight={700}>
            £{cartSummary.subtotal.toFixed(2)}
          </Typography>
        </Box>
        <Box display={'flex'} justifyContent={'space-between'}>
          <Typography color={'text.secondary'}>Discount</Typography>
          <Typography color={'text.secondary'} fontWeight={700}>
            -£{cartSummary.discountAmount.toFixed(2)}
          </Typography>
        </Box>
        <Box display={'flex'} justifyContent={'space-between'}>
          <Typography color={'text.secondary'}>VAT (+20%)</Typography>
          <Typography color={'text.secondary'} fontWeight={700}>
            £{cartSummary.vatAmount.toFixed(2)}
          </Typography>
        </Box>
        <Divider />
        <Box display={'flex'} justifyContent={'space-between'}>
          <Typography variant={'h6'} fontWeight={700}>
            Order total
          </Typography>
          <Typography variant={'h6'} fontWeight={700}>
            £{cartSummary.total.toFixed(2)}
          </Typography>
        </Box>
        <Button
          component="a"
          href={'/ecommerce/checkout'}
          variant={'contained'}
          size={'large'}
          fullWidth
          disabled={loading}
        >
          Checkout
        </Button>
      </Stack>
    </Box>
  );
};

export default SummaryBox;
